<template>
  <transition name="slide-down">
    <div v-if="isVisible" class="update-banner">
      <div class="update-banner__content">
        <div class="update-banner__icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="23 4 23 10 17 10"></polyline>
            <polyline points="1 20 1 14 7 14"></polyline>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
        </div>
        <div class="update-banner__text">
          <strong>{{ bannerTitle }}</strong>
          <span>{{ bannerSubtitle }}</span>
        </div>
        <div class="update-banner__actions">
          <button class="update-banner__btn update-banner__btn--later" @click="remindLater">
            Agora não
          </button>
          <button class="update-banner__btn update-banner__btn--update" @click="updateApp" :disabled="isUpdating || isDownloading || !canUpdate">
            <span v-if="isUpdating">Atualizando...</span>
            <span v-else-if="isDownloading">Baixando...</span>
            <span v-else>Atualizar agora</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'UpdateNotificationBanner',
  props: {
    registration: {
      type: Object,
      default: null,
    },
    versionMismatch: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      show: false,
      isUpdating: false,
      isDownloading: false,
      remindTimer: null,
    };
  },
  computed: {
    isVisible() {
      return this.show;
    },
    bannerTitle() {
      if (this.isDownloading) return 'Baixando atualização... ⏳';
      if (this.versionMismatch && !this.registration) return 'Nova versão detectada! 🚀';
      return 'Nova versão disponível! 🚀';
    },
    bannerSubtitle() {
      if (this.isDownloading) return 'Aguarde o download terminar para atualizar.';
      if (this.versionMismatch && !this.registration) return 'Clique em "Atualizar agora" para recarregar com a nova versão.';
      return 'Atualize para garantir o melhor desempenho do sistema.';
    },
    canUpdate() {
      // Pode atualizar se: tem versionMismatch sem SW, ou tem SW esperando
      if (this.versionMismatch && !this.registration) return true;
      if (this.registration && this.registration.waiting) return true;
      return false;
    },
  },
  watch: {
    registration(val) {
      if (val) {
        if (val.waiting) {
          // SW já está pronto para instalar
          this.isDownloading = false;
          this.show = true;
        } else if (val.installing) {
          // SW ainda está baixando — mostra o banner mas botão desabilitado
          this.isDownloading = true;
          this.show = true;
          // Monitora quando o download terminar
          val.installing.addEventListener('statechange', (e) => {
            if (e.target.state === 'installed') {
              this.isDownloading = false;
            }
          });
        } else {
          this.show = true;
        }
      }
    },
    versionMismatch(val) {
      if (val) {
        this.show = true;
      }
    },
  },
  methods: {
    remindLater() {
      this.show = false;
      this.remindTimer = setTimeout(() => {
        if (this.registration || this.versionMismatch) {
          this.show = true;
        }
      }, 5 * 60 * 1000);
    },

    async updateApp() {
      if (this.isUpdating || this.isDownloading || !this.canUpdate) return;
      this.isUpdating = true;

      try {
        // Limpa todos os caches
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));

        if (this.registration && this.registration.waiting) {
          // Caso 1: SW esperando → ativa e recarrega
          this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          this.registration.waiting.addEventListener('statechange', (e) => {
            if (e.target.state === 'activated') {
              window.location.reload();
            }
          });
          // Fallback
          setTimeout(() => window.location.reload(), 2000);
        } else {
          // Caso 2: versionMismatch sem SW → recarrega direto
          window.location.reload();
        }
      } catch (err) {
        console.error('[UpdateBanner] Erro ao atualizar:', err);
        window.location.reload();
      }
    },
  },
  beforeUnmount() {
    if (this.remindTimer) clearTimeout(this.remindTimer);
  },
};
</script>

<style scoped>
.update-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: linear-gradient(135deg, #5900b2 0%, #fb1789 100%);
  color: white;
  padding: 12px 20px;
  box-shadow: 0 4px 20px rgba(89, 0, 178, 0.4);
}

.update-banner__content {
  display: flex;
  align-items: center;
  gap: 14px;
  max-width: 1200px;
  margin: 0 auto;
  flex-wrap: wrap;
}

.update-banner__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.update-banner__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 200px;
}

.update-banner__text strong {
  font-size: 14px;
  font-weight: 700;
}

.update-banner__text span {
  font-size: 12px;
  opacity: 0.9;
}

.update-banner__actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.update-banner__btn {
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.update-banner__btn--later {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.update-banner__btn--later:hover {
  background: rgba(255, 255, 255, 0.25);
}

.update-banner__btn--update {
  background: white;
  color: #5900b2;
}

.update-banner__btn--update:hover:not(:disabled) {
  background: #f0f0f0;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.update-banner__btn--update:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Animação de entrada */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.35s ease, opacity 0.35s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

@media (max-width: 600px) {
  .update-banner__content {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .update-banner__actions {
    width: 100%;
  }

  .update-banner__btn {
    flex: 1;
    text-align: center;
  }
}
</style>
